import { featureFlag, featureValue } from '@/../app-config.js'
import store from '@/store'
import Vue from 'vue'
import Router from 'vue-router'
import AdvisoryRoutes, {
  AdvisoryRedirect,
  switchAdvisoryName
} from './advisory'
import PortfolioBuilderRoutes from './portfolio-builder'
import AuthenticatedRoutes from './authenticated'
import { PDF_ROUTE_NAMES } from '../constants/pdf-route-name'

Vue.use(Router)

const pages = require('./pages').default
const authenticatedRoutes = AuthenticatedRoutes(pages)
const advisoryRoutes = AdvisoryRoutes(pages, authenticatedRoutes)
const advisoryRedirect = AdvisoryRedirect(authenticatedRoutes)
const portfolioBuilderRoutes = PortfolioBuilderRoutes(pages, authenticatedRoutes)

const componentLogin = {
  'keycloak': pages.loginKeycloakPage,
  'cabp': pages.loginCABPPage,
  'default': pages.loginDefaultPage
}

const mainLogin = featureValue('mainLogin')

const componentLoginDefault = componentLogin[mainLogin]

const loginRoutes = [
  {
    path: 'login',
    name: 'login',
    component: componentLoginDefault,
    meta: { authentication: true }
  }
]

if (featureFlag('secondLogin')) {
  loginRoutes.push(
    {
      path: 'login-email',
      name: 'login-email',
      component: componentLogin['default'],
      meta: { authentication: true }
    }
  )
}

if (featureFlag('enableSmsVerification')) {
  loginRoutes.push(
    {
      path: 'sms-verification',
      name: 'sms-verification',
      props: true,
      component: pages.smsVerification,
      meta: { authentication: true }
    }
  )
}

if (featureFlag('enablePasswordReset')) {
  loginRoutes.push(
    {
      path: 'password-reset',
      name: 'password-reset',
      component: pages.resetPasswordPage,
      meta: { authentication: true }
    }
  )
}

if (featureFlag('enableRegister')) {
  loginRoutes.push(
    {
      path: 'register',
      name: 'register',
      component: pages.registerPage,
      meta: { authentication: true }
    }
  )
}

const router = new Router({
  mode: 'history',
  linkActiveClass: 'active-link',
  scrollBehavior: function (to, from, savedPosition) {
    // scroll to anchor by returning the selector
    if (to.hash) {
      // bypass #anchor check
      if (/^#\d/.test(to.hash) || document.querySelector(to.hash)) {
        const el = document.querySelector(to.hash)
        el.scrollIntoView()

        return { selector: to.hash }
      }
    }

    if (document.getElementById('router-container')) {
      document.getElementById('router-container').scrollTop = 0
    }
    return {x: 0, y: 0}
  },

  routes: [
    ...(featureFlag('enableWelcomePage')
      ? [{
        path: '/welcome-page',
        name: 'welcome-page',
        component: pages.welcomePage
      }]: []
    ),
    {
      path: '/',
      name: 'landing-page',
      redirect: {
        name: 'authenticated'
      }
    },
    {
      path: '/arbitrations/:arbitrationId/pdf',
      name: 'arbitrations-pdf',
      component: pages.advisoryArbitrationPdf
    },
    {
      path: '/model-portfolio/:modelPortfolioId/pdf',
      name: 'model-porfolio-pdf',
      component: pages.portfolioBuilderModelPortfolioPdf
    },
    {
      path: '/estate-report/:userId/:wealthDecisionId/pdf',
      name: 'estate-report-pdf',
      component: pages.customerEstateReportPdf
    },
    {
      path: '/pdf/managed-portfolio-reporting/:portfolioId',
      name: 'managed-portfolio-reporting-pdf',
      component: pages.managedPortfolioReportingPdf
    },
    {
      path: '/maintenance',
      name: 'maintenance',
      component: pages.maintenance
    },
    {
      path: '/loading',
      name: 'loading',
      component: pages.loading
    },
    {
      path: '/delete-profile',
      name: 'delete-profile',
      component: pages.deleteProfile
    },
    ...(mainLogin === 'cabp'
      ? [{
        path: '/cabp_collab_auth',
        name: 'cabp-collab-auth-container',
        component: pages.lobbyContainer,
        props: true,
        children: [
          {
            path: '/',
            name: 'cabp-collab-auth',
            component: pages.loginCabpAuth,
            props: true,
            meta: {
              type: 'collab'
            }
          }
        ]
      }]: []
    ),
    ...(mainLogin === 'cabp'
      ? [{
        path: '/cabp_client_auth',
        name: 'cabp-client-auth-container',
        component: pages.lobbyContainer,
        props: true,
        children: [
          {
            path: '/',
            name: 'cabp-client-auth',
            component: pages.loginCabpAuth,
            props: true,
            meta: {
              type: 'client'
            }
          }
        ]
      }]: []
    ),
    ...(mainLogin === 'keycloak'
      ? [{
        path: '/auth/exchange',
        name: 'keycloak-callback-container',
        component: pages.lobbyContainer,
        props: true,
        children: [
          {
            path: '/',
            name: 'keycloak-callback',
            component: pages.loginKeycloakCallbackPage,
            props: true,
            meta: {
              type: 'client'
            }
          }
        ]
      }]: []
    ),
    {
      path: '/app',
      name: 'authenticated',
      component: pages.authenticatedPage,
      meta: {
        authenticated: true
      },
      redirect: {
        name: 'dashboard'
      },
      children: authenticatedRoutes
    },
    {
      path: '/app',
      component: pages.lobbyContainer,
      meta: {
        authenticated: true
      },
      children: [
        {
          path: 'accounts/add',
          component: pages.accountsAdd,
          children: [
            {
              path: '',
              name: 'accounts-add-index',
              component: pages.accountsAddIndex,
              meta: {
                hideMenu: true
              }
            },
            {
              path: ':bankId',
              name: 'accounts-add-form',
              component: pages.accountsAddForm,
              props: true,
              meta: {
                hideMenu: true
              }
            }
          ]
        },
        {
          path: 'load',
          name: 'accounts-load',
          component: pages.accountsLoad,
          props: true
        },
        {
          path: 'profile',
          name: 'profile-fill',
          component: pages.profileFill,
          redirect: {name: 'profile-fill-risk'},
          children: [
            {
              path: 'risk',
              name: 'profile-fill-risk',
              component: pages.profileFillRisk
            },
            {
              path: 'infos',
              name: 'profile-fill-infos',
              component: pages.profileFillInfos,
              props: true
            },
            {
              path: 'result',
              name: 'profile-result',
              component: pages.profileResult
            }
          ]
        },
        {
          path: 'accounts/risk',
          name: 'accounts-risk',
          component: pages.accountsRisk
        },
        {
          path: 'subscribe',
          name: 'subscribe',
          component: pages.subscriptionPage
        },
        {
          path: 'pay',
          name: 'pay',
          component: pages.paymentPage,
          props: true
        }
      ]
    },
    {
      path: '/app',
      name: 'unauthenticated',
      component: pages.lobbyContainer,
      meta: {
        authenticated: false
      },
      children: [
        ...loginRoutes,
        {
          path: 'cgu',
          name: 'cgu',
          component: pages.cgu
        },
      ]
    },
    {
      path: '/advisory',
      name: 'advisory-unauthenticated',
      component: pages.lobbyContainer,
      meta: {
        advisory: true,
        authenticated: false
      },
      redirect: {
        name: 'advisory-login'
      },
      children: [
        {
          path: 'login',
          name: 'advisory-login',
          component: pages.advisoryLoginPage,
          redirect: {
            name: 'login'
          }
        }
      ]
    },
    advisoryRoutes,
    ...(featureFlag('aEnablePortfolioBuilder') 
      ? [portfolioBuilderRoutes]
      : []
    ),
    {
      path: '/500',
      name: 'error500',
      component: pages.error500
    },
    {
      path: '/404',
      name: 'error404',
      component: pages.error404
    },
    {
      path: '*',
      redirect: {
        name: 'error404'
      }
    }
  ]
})

const getAdvisoryRouteNames = (routes) => {
  const res = []

  for (const route of routes) {
    if (route?.name) res.push(route.name)

    if (route?.children) {
      const test = getAdvisoryRouteNames(route.children)
      res.push(...test)
    }
  }

  return res 
}

const advisoryCustomerAuthenticatedRoutes = authenticatedRoutes.map(route =>
  switchAdvisoryName(route)
)

const notAllowedRouteNames = getAdvisoryRouteNames(advisoryCustomerAuthenticatedRoutes)

router.beforeEach(async (to, from, next) => {
  const { matched } = to
  // Auth
  const accessToken = store.getters['auth/accessToken']
  const hasRole = store.getters['auth/hasRole']
  const authenticatedCustomer = hasRole === 'customer'
  const authenticatedAdvisor = hasRole === 'advisor'
  const authenticated = Boolean(accessToken)

  // Features
  const maintenanceMode = featureFlag('enableMaintenancePage')
  const conseilEnabled = featureFlag('enableConseil')
  const aInviteClientsEnabled = featureFlag('aInviteClients')
  const aInteractions = featureFlag('aInteractions')
  const allowUserRegistration = featureFlag('allowUserRegistration')
  const disableAdvisoryDashboardRoutes = featureFlag('aDisableCustomerView')
  const aEnableWelcomePage = featureFlag('enableWelcomePage') && featureFlag('enableRedirectionWelcomePage')

  if (authenticatedAdvisor && disableAdvisoryDashboardRoutes) {
    notAllowedRouteNames.find(routeName => routeName === to.name) &&
      next({ name: 'error404' })
  }

  if (PDF_ROUTE_NAMES.includes(to.name)) {
    store.commit('auth/saveTokensWithSetHeaderApi', { accessToken: to.query.token, refreshToken: '' })
  }

  if (maintenanceMode) {
    if (to.name === 'maintenance') {
      return next()
    } else {
      return next({ name: 'maintenance' })
    }
  }

  if (!conseilEnabled) {
    // If Conseil is disabled disallow access to other areas of platform
    if (!authenticatedAdvisor) {
      if (matched[0].path !== '/advisory') {
        return next({ name: 'advisory-login' })
      }
    }
    if (authenticatedAdvisor && matched[0].path !== '/advisory') {
      return next({ name: 'advisory-authenticated' })
    }
  }

  if (!aInviteClientsEnabled) {
    if (to.name === 'customers-add') {
      return next({ name: 'advisory-authenticated' })
    }
  }

  if (!allowUserRegistration) {
    if (to.name === 'register') {
      return next({ name: 'login' })
    }
  }

  if (!aInteractions) {
    if (to.name === 'customer-interactions') {
      return next({ name: 'advisory-authenticated' })
    }
  }

  if (to.name === 'accounts-load' && from.name !== 'accounts-add-form') {
    // Redirects to dashboard/accounts-add page if going back to accounts-load page
    return next({
      name: 'authenticated'
    })
  }

  if (authenticatedAdvisor && advisoryRedirect.indexOf(to.name) >= 0) {
    return next({
      name: `advisor-${to.name}`,
      params: to.params,
      hash: to.hash,
      query: to.query
    })
  }

  // If route needs credentials and the user has none do a redirect to login
  if (!authenticated) {
    if (matched[0].path === '/advisory' || matched[0].path === '/app' || matched[0].name === 'welcome-page') {
      if (!to.meta.authentication) {
        return next({
          name: 'login',
          query: {
            redirect: to.fullPath
          }
        })
      }
    }
  }

  if (authenticated) {
    // Redirect homepage to user role homepage

    if (to.path === '/') {
      if (authenticatedCustomer) {
        return next({ name: 'authenticated' })
      }
      if (authenticatedAdvisor) {
        await store.dispatch('advisor/fetchUser')
        const isStatisticsOnly = store.getters['advisor/isStatisticsOnly']

        if (isStatisticsOnly) return next({ name: 'advisory-statistics' })

        if (aEnableWelcomePage) {
          const hasSeenWelcomePage = store.getters['advisor/hasSeenWelcomePage']

          if (!hasSeenWelcomePage) {
            if (matched[0].name !== 'welcome-page') {
              return next({ name: 'welcome-page' })
            }
          }
        }

        return next({ name: 'advisory-authenticated' })
      }
    } else if (authenticatedAdvisor) {
      await store.dispatch('advisor/fetchUser')
      const isStatisticsOnly = store.getters['advisor/isStatisticsOnly']

      if (isStatisticsOnly && !to.meta.isStatisticsAdvisoryAuthorized) {
        return next({ name: 'advisory-statistics' })
      }

      if (aEnableWelcomePage) {
        const hasSeenWelcomePage = store.getters['advisor/hasSeenWelcomePage']

        if (!hasSeenWelcomePage) {
          if (matched[0].name !== 'welcome-page') {
            return next({ name: 'welcome-page' })
          }
        }
      }
    }

    // Disallow access to login if user is authenticated
    if (to.meta.authentication) {
      return next({ path: '/' })
    }

    // Disallow access to other areas of website for respective roles
    if (matched[0].path === '/advisory' && authenticatedCustomer) {
      return next({ path: '/' })
    }

    if (matched[0].path === '/app' && authenticatedAdvisor) {
      return next({ name: '/' })
    }
  }

  next()
})

export default router
